/**
 * OAuth 2.1 consent page — the portal half of plan D8.
 *
 * CP's `GET /api/v1/oauth/authorize` validates the client's request, parks it
 * in `cp01.OAuthAuthorizationRequest` and 302s the browser to
 * `<frontend>/oauth/consent?request_id=…`. This page is where the user reads
 * who is asking, picks WHICH organisation the session will be bound to, and
 * approves or denies. The answer goes back to
 * `POST /api/v1/oauth/authorize/decision`, which is the only place an
 * authorisation code is minted; we then hand the browser to the `redirect_uri`
 * CP returns.
 *
 * Deliberate properties:
 *
 * - **It lives outside `/portal`** because CP's `CONSENT_PATH` is a fixed
 *   `/oauth/consent` and the client's flow is not a portal navigation. It is
 *   still login-gated — by `RequireAuth` here rather than by the portal layout
 *   — and `/oauth/consent` is on the `isSafePortalRedirect` allow-list so the
 *   sign-in round-trip comes back to the flow instead of dumping the user on
 *   the dashboard mid-authorisation.
 * - **The organisation picker only ever offers `entitled` organisations.** The
 *   server answers that question with the same gate the decision endpoint runs
 *   (RBAC + suspension + subscription), so offering a `false` would produce a
 *   403 one click later. A user with none is told so — naming the resource,
 *   never a resource-specific reason the server did not send — and Approve is
 *   not reachable.
 * - **No auto-approve, ever.** Every authorisation shows this page (D7), and
 *   nothing on it acts before the user clicks.
 * - **A failure is NEVER a silent redirect.** CP's OAuth errors are RFC 6749
 *   objects, not FastAPI `detail`s, so we read them with
 *   `oauthErrorDescription()` and show them; `redirect_uri` is honoured only
 *   when CP actually returned one.
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { createFileRoute, Navigate, useLocation } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Loader2,
  ShieldCheck,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { isSafePortalRedirect } from "@/lib/auth-redirect";
import { PortalApiError } from "@/lib/portalApi";
import {
  oauthConsentApi,
  oauthErrorDescription,
  selectableOrganisations,
  type ConsentContext,
} from "@/services/api/oauthConsent";

export const Route = createFileRoute("/oauth/consent")({
  component: OAuthConsentPage,
  validateSearch: (search: Record<string, unknown>) => ({
    request_id: typeof search.request_id === "string" ? search.request_id : "",
  }),
});

function OAuthConsentPage() {
  const { token, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { request_id: requestId } = Route.useSearch();

  // 🔴 Captured ONCE, at mount. `useLocation()` reports the PENDING location
  // while a navigation is in flight, and this component is still mounted
  // through those renders — reading it live made each render re-issue the
  // redirect with the previous sign-in URL inside it, nesting
  // `redirect=/portal/sign-in?redirect=…` dozens of levels deep until the URL
  // was unusable (measured by tests/playwright/platform/oauth-consent.spec.ts
  // before this line existed). A frozen value re-navigates to the same place,
  // which is a no-op. The allow-list check is the same one RequireAuth makes;
  // `/oauth/consent` is on it (EXACT_ALLOWED_PATHS in lib/auth-redirect.ts).
  const [signInTarget] = useState(() =>
    isSafePortalRedirect(location.href) ? location.href : undefined,
  );

  if (isLoading) {
    return (
      <ConsentFrame>
        <Spinner label="Loading…" />
      </ConsentFrame>
    );
  }

  if (!isAuthenticated || !token) {
    return (
      <Navigate
        to="/portal/sign-in"
        search={{ redirect: signInTarget }}
        replace
      />
    );
  }

  return (
    <ConsentFlow
      token={token}
      requestId={requestId}
      signInTarget={signInTarget}
    />
  );
}

function ConsentFlow({
  token,
  requestId,
  signInTarget,
}: {
  token: string;
  requestId: string;
  signInTarget: string | undefined;
}) {
  const [context, setContext] = useState<ConsentContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [organisationId, setOrganisationId] = useState<string>("");
  const [submitting, setSubmitting] = useState<"approve" | "deny" | null>(null);
  const [leaving, setLeaving] = useState(false);
  // A 401 here needs the sign-in round-trip, not an error message.
  // `portalFetch` only auto-redirects on paths under /portal — this page is
  // not one, so an expired session would otherwise dead-end on "we could not
  // load this request" with the flow still parked and no way back into it.
  const [reauth, setReauth] = useState(false);

  useEffect(() => {
    if (!requestId) {
      setLoadError(
        "This link is missing its request reference. Start the connection again from your Claude client.",
      );
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const ctx = await oauthConsentApi.getContext(token, requestId);
        if (cancelled) return;
        setContext(ctx);
        // Pre-select ONLY when there is exactly one entitled organisation —
        // with several, the user must state which tenant the token carries.
        const selectable = selectableOrganisations(ctx);
        if (selectable.length === 1) setOrganisationId(selectable[0]!.id);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof PortalApiError && err.status === 401) {
          setReauth(true);
          return;
        }
        setLoadError(
          oauthErrorDescription(err) ??
            "We could not load this authorisation request. It may have expired — start the connection again from your Claude client.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, requestId]);

  const decide = useCallback(
    async (approve: boolean) => {
      setDecisionError(null);
      setSubmitting(approve ? "approve" : "deny");
      try {
        const result = await oauthConsentApi.decide(
          token,
          requestId,
          approve,
          approve ? organisationId : undefined,
        );
        if (!result?.redirect_uri) {
          setDecisionError(
            "The authorisation server did not return a destination. Start the connection again from your Claude client.",
          );
          return;
        }
        // Hand the browser back to the client's callback. A full-page
        // assignment, not a router navigate: the target is the OAuth client's
        // own origin (claude.ai, or a loopback listener), not a portal route.
        setLeaving(true);
        window.location.assign(result.redirect_uri);
      } catch (err) {
        if (err instanceof PortalApiError && err.status === 401) {
          setReauth(true);
          return;
        }
        setDecisionError(
          oauthErrorDescription(err) ??
            "We could not record your answer. Please try again.",
        );
      } finally {
        setSubmitting(null);
      }
    },
    [token, requestId, organisationId],
  );

  if (reauth) {
    return (
      <Navigate
        to="/portal/sign-in"
        search={{ redirect: signInTarget }}
        replace
      />
    );
  }

  if (loadError) {
    return (
      <ConsentFrame>
        <div
          role="alert"
          className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <p>{loadError}</p>
        </div>
      </ConsentFrame>
    );
  }

  if (!context) {
    return (
      <ConsentFrame>
        <Spinner label="Loading the request…" />
      </ConsentFrame>
    );
  }

  const selectable = selectableOrganisations(context);
  const hasSelectable = selectable.length > 0;
  const busy = submitting !== null || leaving;

  return (
    <ConsentFrame>
      <div className="space-y-5">
        <div className="space-y-1">
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">
              {context.client_name}
            </span>{" "}
            is asking to connect to{" "}
            <span className="font-semibold text-slate-900">
              {context.resource_name}
            </span>{" "}
            on your behalf.
          </p>
          {context.client_uri && (
            <p className="text-xs text-slate-500 break-all">
              {context.client_uri}
            </p>
          )}
        </div>

        {/* The one identifier that cannot be faked. Everything above it —
            name, URI, logo — is free text from an unauthenticated client
            registration, so a page without this cannot distinguish the real
            Claude from a look-alike that delivers the code somewhere else. */}
        {context.client_redirect_host && (
          <div className="rounded-md border border-slate-200 bg-white p-3">
            <p className="text-xs text-slate-500">
              The result will be sent to
            </p>
            <p className="break-all font-mono text-sm font-semibold text-slate-900">
              {context.client_redirect_host}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Only approve if you recognise this. Anyone can name themselves
              anything here; this is the address that actually receives access.
            </p>
          </div>
        )}
        <div className="space-y-1">
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            It will be able to
          </p>
          <ul className="space-y-2">
            {context.scopes.map((scope) => (
              <li
                key={scope.name}
                className="flex items-start gap-2 text-sm text-slate-700"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue-medium" />
                <span>{scope.description}</span>
              </li>
            ))}
          </ul>
        </div>

        {hasSelectable ? (
          <div className="space-y-2">
            <Label htmlFor="consent-organisation">Organisation</Label>
            <Select
              value={organisationId}
              onValueChange={setOrganisationId}
              disabled={busy}
            >
              <SelectTrigger id="consent-organisation" className="w-full">
                <SelectValue placeholder="Choose an organisation" />
              </SelectTrigger>
              <SelectContent>
                {selectable.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="flex items-start gap-2 text-xs text-slate-500">
              <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                The connection can read data for this organisation only. To use
                another, connect again and pick it here.
              </span>
            </p>
          </div>
        ) : (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            {/* Generic on purpose. The consent context carries `entitled` per
                organisation but NOT which gate refused it (role, organisation
                state, subscription or pilot enrolment — CP `get_consent_context`),
                and the gates differ per resource. Naming one reason here would
                be a guess: the previous copy named the SQL RAG gates for every
                resource, so an org_member refused Lara Workflows by ROLE was
                told to find a knowledgebase permission. */}
            <p>
              None of your organisations can grant access to{" "}
              <span className="font-semibold">{context.resource_name}</span>.
              Either your role does not allow it, or the organisation is not
              set up for it (for example, a subscription is not active) — ask
              an organisation owner or admin, then try again.
            </p>
          </div>
        )}

        {decisionError && (
          <div
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          >
            {decisionError}
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={() => void decide(false)}
            disabled={busy}
          >
            {submitting === "deny" ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cancelling…
              </>
            ) : (
              "Deny"
            )}
          </Button>
          <Button
            onClick={() => void decide(true)}
            disabled={busy || !hasSelectable || !organisationId}
          >
            {submitting === "approve" ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Approving…
              </>
            ) : (
              "Approve"
            )}
          </Button>
        </div>

        <p className="text-xs text-slate-500">
          To end this connection, open{" "}
          <span className="font-medium">
            Knowledgebase Database → Connect Claude
          </span>{" "}
          in the Lara portal and press{" "}
          <span className="font-medium">Revoke</span> under{" "}
          <span className="italic">Your Claude connections</span>. Claude loses
          access within about a minute. Removing the connector in Claude alone
          may not end it.
        </p>
      </div>
    </ConsentFrame>
  );
}

function ConsentFrame({ children }: { children: ReactNode }) {
  return (
    <>
      <Helmet>
        <title>Authorise access | Larasoft</title>
        {/* A consent screen must never be indexed or embedded. */}
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-brand-blue-medium" />
              Authorise access
            </CardTitle>
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      </div>
    </>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
      <Loader2 className="h-5 w-5 animate-spin" />
      {label}
    </div>
  );
}
